import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

function toReadableMatchDate(isoDate) {
  if (!isoDate) return "-";
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString();
}

function getTargetMatchOpponent(item) {
  if (!item || typeof item !== "object") return null;

  const directKeys = [
    "opponent",
    "opponent_name",
    "adversaire",
    "adversary",
    "opposition",
    "versus",
    "vs",
  ];

  for (const key of directKeys) {
    const value = item?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  if (typeof item?.label === "string") {
    const label = item.label.trim();
    const lower = label.toLowerCase();
    for (const separator of [" vs ", " v ", " contre ", " - "]) {
      const idx = lower.indexOf(separator);
      if (idx === -1) continue;
      const candidate = label.slice(idx + separator.length).trim();
      if (candidate) return candidate;
    }
  }

  return null;
}

function getTargetMatchStatusText(item, t) {
  if (!item) return "";

  if (item?.player_has_match_data === false) {
    return t("weekly_recap.target_match.no_player_data", {
      defaultValue: "no player match data",
    });
  }

  if (item?.is_selectable && item?.is_latest_completed) {
    return t("weekly_recap.target_match.latest_played", {
      defaultValue: "latest played",
    });
  }

  switch (item?.status) {
    case "upcoming":
      return t("weekly_recap.target_match.upcoming", { defaultValue: "upcoming" });
    case "microcycle_in_progress":
      return t("weekly_recap.target_match.in_progress", { defaultValue: "microcycle in progress" });
    case "missing_data":
      return t("weekly_recap.target_match.missing_data", { defaultValue: "missing match data" });
    case "locked":
      return t("weekly_recap.target_match.unavailable", { defaultValue: "not selectable" });
    default:
      return "";
  }
}

function getTargetMatchOptionLabel(item, t) {
  const dateText = toReadableMatchDate(item?.date);
  const opponentName = getTargetMatchOpponent(item);
  const opponent = opponentName
    ? t("weekly_recap.target_match.option_against", {
      defaultValue: "vs {{opponent}}",
      opponent: opponentName,
    })
    : "";
  const statusText = getTargetMatchStatusText(item, t);
  const suffix = statusText ? ` (${statusText})` : "";
  return `${dateText}${opponent ? ` - ${opponent}` : ""}${suffix}`;
}

export default function WeeklyRecapTargetMatchSelect({
  value,
  matches = [],
  opponent,
  loading = false,
  isDarkMode,
  onChange,
}) {
  const { t } = useTranslation();

  const groupedTargetMatches = useMemo(() => {
    const groups = {};
    for (const item of matches) {
      const dateValue = item?.date;
      if (!dateValue) continue;
      const d = new Date(`${dateValue}T00:00:00`);
      if (Number.isNaN(d.getTime())) continue;

      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
      if (!groups[key]) groups[key] = { key, label, items: [] };
      groups[key].items.push(item);
    }

    return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
  }, [matches]);

  const selectedOpponent = opponent
    || getTargetMatchOpponent(matches.find((item) => item?.date === value));

  return (
    <div className="min-w-0 w-full sm:w-auto">
      <label className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider opacity-70">
        {t("weekly_recap.target_match.label", { defaultValue: "Target match" })}
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-500" />
        ) : null}
      </label>
      <div className="relative">
        <select
          value={value || ""}
          onChange={(event) => onChange?.(event.target.value || null)}
          disabled={loading || matches.length === 0}
          className={`w-full min-w-[220px] rounded-lg border px-2.5 py-2 text-sm ${
            isDarkMode
              ? "border-gray-700 bg-gray-900 text-gray-100"
              : "border-gray-300 bg-white text-gray-900"
          } disabled:cursor-wait disabled:opacity-70`}
        >
          {matches.length === 0 ? (
            <option value="">
              {t("weekly_recap.target_match.empty", { defaultValue: "No completed match" })}
            </option>
          ) : null}
          {matches.length > 0 && !value ? (
            <option value="" disabled>
              {t("weekly_recap.target_match.no_selection", {
                defaultValue: "No selectable target match yet",
              })}
            </option>
          ) : null}
          {groupedTargetMatches.map((group) => (
            <optgroup key={group.key} label={group.label}>
              {group.items.map((item) => {
                const dateValue = item?.date || "";
                return (
                  <option
                    key={dateValue}
                    value={dateValue}
                    disabled={!item?.is_selectable}
                  >
                    {getTargetMatchOptionLabel(item, t)}
                  </option>
                );
              })}
            </optgroup>
          ))}
        </select>
      </div>
      <p className="mt-1 text-[11px] opacity-70">
        {loading
          ? t("weekly_recap.loading_match", {
            defaultValue: "Loading match data...",
          })
          : value
            ? t("weekly_recap.target_match.selected_context", {
              defaultValue: "Selected target: {{date}} {{opponent}}",
              date: toReadableMatchDate(value),
              opponent: selectedOpponent
                ? t("weekly_recap.target_match.selected_against", {
                  defaultValue: "(vs {{opponent}})",
                  opponent: selectedOpponent,
                })
                : "",
            })
            : t("weekly_recap.target_match.no_selection", {
              defaultValue: "No selectable target match yet",
            })}
      </p>
    </div>
  );
}
